// src/services/googleAuthService.ts

import { API_URLS } from "../api/urls";
import { EnhancedTokenManager } from "../utils/tokenManager";
import { SubdomainManager } from "../utils/subdomainUtils";

interface GoogleAuthResponse {
  message: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  onboarded: boolean;
  user: {
    id: number;
    email: string;
    full_name: string;
    profile_image: string | null;
    auth_provider: string;
  };
}

interface GoogleAuthResult {
  success: boolean;
  needsOnboarding?: boolean;
  userProfile?: any;
  error?: string;
}

export class GoogleAuthService {
  private static getBaseDomain(): string {
    const hostname = window.location.hostname;
    if (hostname.includes("cluva.in")) return "cluva.in";
    return "cluva.io";
  }

  private static getAppDomain(): string {
    return `app.${this.getBaseDomain()}`;
  }

  /**
   * Authenticate with backend using the Google ID token obtained from GIS.
   * The id_token JWT (from Sign In With Google) already contains name + picture
   * in its payload, so no separate userinfo API call is needed.
   */
  static async authenticateWithBackend(
    googleIdToken: string,
    googleUserInfo?: { name?: string; picture?: string; email?: string } | null,
  ): Promise<GoogleAuthResult> {
    try {
      const full_name = googleUserInfo?.name || null;
      const profile_image = googleUserInfo?.picture || null;

      const response = await fetch(API_URLS.GOOGLE_OAUTH_REQUEST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: googleIdToken, full_name, profile_image }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        let errorMessage = "Authentication failed";
        if (response.status === 401) errorMessage = "Invalid authentication token. Please try signing in again.";
        else if (response.status === 403) errorMessage = "Access forbidden. Please contact support.";
        else if (response.status === 500) errorMessage = "Server error. Please try again later.";
        else if (errorData.message || errorData.detail) errorMessage = errorData.message || errorData.detail;

        return { success: false, error: errorMessage };
      }

      const data: GoogleAuthResponse = await response.json();

      EnhancedTokenManager.setTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        token_type: data.token_type || "bearer",
      });

      const needsOnboarding = !data.onboarded;
      const userData = data.user;

      if (userData.profile_image) {
        window.dispatchEvent(
          new CustomEvent("profile-image-updated", {
            detail: { immediateImageUrl: userData.profile_image },
          }),
        );
        localStorage.setItem("user_profile_image", userData.profile_image);
      } else if (profile_image) {
        window.dispatchEvent(
          new CustomEvent("profile-image-updated", {
            detail: { immediateImageUrl: profile_image },
          }),
        );
        localStorage.setItem("user_profile_image", profile_image);
      }

      const profileResult = await EnhancedTokenManager.fetchAndCheckUserProfile(data.access_token);
      let finalUserProfile = userData;

      if (profileResult.success && profileResult.profile) {
        const fullProfile = profileResult.profile;
        finalUserProfile = { ...userData, ...fullProfile };

        if (fullProfile.workspace) {
          EnhancedTokenManager.setWorkspaceDataForUser(userData.id, fullProfile.workspace);
        } else if (fullProfile.workspace_id) {
          EnhancedTokenManager.setWorkspaceIdForUser(userData.id, fullProfile.workspace_id);
        }
      }

      return { success: true, needsOnboarding, userProfile: finalUserProfile };
    } catch (error) {
      let errorMessage = "Network error occurred";
      if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
        errorMessage = "Network error. Please check your internet connection.";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Navigate user after successful Google authentication.
   */
  static async navigateAfterAuth(
    needsOnboarding: boolean,
    userProfile: any | null,
    navigate: (path: string, options?: any) => void,
    locationState?: any,
  ): Promise<void> {
    try {
      let returnUrl = locationState?.returnUrl;
      let returnStateData = locationState?.returnState;

      if (!returnUrl && (window as any).__pending_deep_link_info) {
        const pendingInfo = (window as any).__pending_deep_link_info;
        returnUrl = pendingInfo.returnUrl;
        returnStateData = pendingInfo.returnState;
      }

      if (needsOnboarding) {
        navigate("/onboarding", {
          replace: true,
          state: {
            email: userProfile?.email,
            fullName: userProfile?.full_name || userProfile?.displayName,
            fromSignUp: true,
            returnUrl,
            returnState: returnStateData,
          },
        });
        return;
      }

      if (returnUrl && returnUrl.includes("/view-card/")) {
        const cardIdMatch = returnUrl.match(/\/view-card\/(\d+)/);
        const cardId = cardIdMatch ? parseInt(cardIdMatch[1]) : null;

        if (cardId) {
          navigate("/shared-feedback-view", {
            replace: true,
            state: {
              cardId,
              fromDeepLink: true,
              returnToDashboard: true,
              developerEmail: returnStateData?.developerEmail,
              activeTab: "all",
              fromAuth: true,
            },
          });
          sessionStorage.removeItem("pending_deep_link");
          delete (window as any).__pending_deep_link_info;
          return;
        }
      }

      const isOnSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
      const currentSubdomain = SubdomainManager.getCurrentSubdomain();

      if (userProfile?.workspace?.subdomain) {
        const extractedSubdomain = SubdomainManager.extractSubdomainFromUrl(userProfile.workspace.subdomain);

        if (extractedSubdomain) {
          SubdomainManager.clearWorkspaceSubdomain();
          await new Promise((resolve) => setTimeout(resolve, 50));
          SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);

          if (isOnSubdomain && currentSubdomain === extractedSubdomain) {
            navigate("/dashboard", { replace: true, state: { fromAuth: true } });
            return;
          }

          if (SubdomainManager.isLocalDevelopment()) {
            navigate("/dashboard", { replace: true, state: { fromAuth: true } });
          } else {
            await new Promise((resolve) => setTimeout(resolve, 100));
            SubdomainManager.navigateToSubdomain(extractedSubdomain, "/dashboard", true);
          }
          return;
        }
      }

      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (accessToken) {
          const response = await fetch(API_URLS.GET_WORKSPACES, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const wsData = await response.json();
            if (wsData.workspaces?.length > 0) {
              const workspace = wsData.workspaces[0];

              if (workspace.subdomain) {
                const extractedSubdomain = SubdomainManager.extractSubdomainFromUrl(workspace.subdomain);

                if (extractedSubdomain) {
                  SubdomainManager.clearWorkspaceSubdomain();
                  await new Promise((resolve) => setTimeout(resolve, 50));
                  SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);

                  if (userProfile?.id) {
                    EnhancedTokenManager.setWorkspaceDataForUser(userProfile.id, workspace);
                  }

                  if (isOnSubdomain && currentSubdomain === extractedSubdomain) {
                    navigate("/dashboard", { replace: true, state: { fromAuth: true } });
                    return;
                  }

                  if (SubdomainManager.isLocalDevelopment()) {
                    navigate("/dashboard", { replace: true, state: { fromAuth: true } });
                  } else {
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    SubdomainManager.navigateToSubdomain(extractedSubdomain, "/dashboard", true);
                  }
                  return;
                }
              }
            }
          }
        }
      } catch (error) {
        console.warn("Failed to fetch workspace subdomain:", error);
      }

      if (SubdomainManager.isLocalDevelopment() || !isOnSubdomain) {
        navigate("/dashboard", { replace: true, state: { fromAuth: true } });
      } else {
        const appDomain = this.getAppDomain();
        window.location.replace(`https://${appDomain}/dashboard`);
      }
    } catch (error) {
      console.error("Navigation error:", error);
      navigate("/dashboard", { replace: true, state: { fromAuth: true } });
    }
  }
}
